import { GiveawayService } from '../services/giveaway.service';

export default {
  namespaced: true,

  state: {
    create: false,
    edit: false,
    list: false,
    giveaways: [],
    giveaway: null,
    giveawayInterval: null,
    active: null,
  },

  getters: {
    giveaways: state => state.giveaways,
    giveaway: state => state.giveaway,
    active: state => state.active,
    list: state => state.list,
    create: state => state.create,
    edit: state => state.edit,
  },

  mutations: {
    setActive(state, giveaway) {
      state.active = giveaway;
    },
    setGiveaways(state, giveaways) {
      state.giveaways = giveaways;
    },
    listGiveaways(state, bool) {
      state.list = bool;
    },
    editGiveaway(state, bool) {
      state.edit = bool;
    },
    createGiveaway(state, bool) {
      state.create = bool;
    },
    setGiveaway(state, giveaway) {
      state.giveaway = giveaway;
    },
  },

  actions: {
    /**
     * Handle incoming giveaway events from eventstream
     * @param {Object} param0
     * @param {Function} param0.commit
     * @param {Object} param0.state
     * @param {Object} param1
     */
    handleEvents: ({ commit, state }, { id, events }) => {
      const publishedArr = [...new Map(events.filter(event => event.event === 'giveaway.published' || event.event === 'giveaway.closed').map(event => ({ ...event.payload, timestamp: event.timestamp, state: 'published' })).map(event => [event.id, event])).values()];
      const completedArr = events.filter(event => event.event === 'giveaway.completed' || event.event === 'giveaway.unpublished').map(event => ({ ...event.payload, timestamp: event.timestamp, state: 'unpublished' }));

      let published = publishedArr.pop();

      if (!published) {
        if (state.active) {
          published = state.active;
        }
      }

      if (published) {
        const unpublishIndex = completedArr.findIndex(g => g.id === published.id && new Date(g.timestamp) > new Date(published.timestamp));
        if (unpublishIndex !== -1) {
          published = null;
        }
      }

      events.forEach((event) => {
        let messageState = '';

        switch (event.event) {
          case 'giveaway.published':
            messageState = 'published';
            break;
          case 'giveaway.completed':
            messageState = 'winners drawn';
            break;
          case 'giveaway.unpublished':
            messageState = 'unpublished';
            break;
          case 'giveaway.closed':
            messageState = 'closed';
            break;
          default:
            break;
        }
        commit('Chat/addMessage', {
          id,
          message: {
            message_id: event.message_id,
            event: event.event,
            timestamp: event.timestamp,
            message: `${event.payload.title} ${messageState}`,
            payload_id: event.payload.id,
          },
        }, { root: true });
      });

      commit('setActive', published);
    },
    getAllGiveaways: async (_, params = {}) => {
      let payload = {
        page: 1,
        per_page: 20,
        expand: 'broadcast',
      };

      payload = { ...payload, ...params };

      try {
        const giveaways = await GiveawayService.giveaways(payload);
        return giveaways;
      } catch (e) {
        return false;
      }
    },
    /**
     * Get a list of giveaways for broadcast with the given id
     * @param {Object} param0
     * @param {Function} param0.commit
     * @param {String} id
     * @returns {Boolean}
     */
    getList: async ({ commit }, params = {}) => {
      let payload = {
        page: 1,
        per_page: 100,
      };

      payload = { ...payload, ...params };

      try {
        const { giveaways } = await GiveawayService.giveaways(payload);
        commit('setGiveaways', giveaways);
        return giveaways;
      } catch (e) {
        return [];
      }
    },
    getGiveaway: async ({ commit, state, dispatch }, { id, doCommit = true }) => {
      try {
        const giveaway = await GiveawayService.get(id);
        if (doCommit) {
          commit('setGiveaway', giveaway);
          if (!state.giveawayInterval && giveaway.state === 'published') {
            state.giveawayInterval = setInterval(() => {
              dispatch('getGiveaway', { id });
            }, 5000);
          }
          return true;
        }
        return giveaway;
      } catch (e) {
        if (doCommit) {
          return false;
        }
        return null;
      }
    },
    /**
     * Clear giveaway states
     * @param {Object} param0
     * @param {Function} param0.commit
     */
    clearGiveaway: ({ commit, state }) => {
      commit('editGiveaway', false);
      commit('createGiveaway', false);
      commit('setGiveaway', null);
      clearInterval(state.giveawayInterval);
      state.giveawayInterval = null;
    },
    createGiveaway: async ({ dispatch }, { id, payload }) => {
      try {
        const giveaway = await GiveawayService.create(id, payload);
        dispatch('getGiveaway', { id: giveaway.id });
        return true;
      } catch (e) {
        return false;
      }
    },
    /**
     * Update giveaway with {id} with {payload}
     * @param {Object} param0
     * @param {Function} param0.commit
     * @param {Object} param0.state
     * @param {Object} param1
     * @param {String} param1.id
     * @param {Object} param1.payload
     * @returns {Boolean}
     */
    updateGiveaway: async ({ commit, state }, { id, payload }) => {
      try {
        await GiveawayService.update(id, payload);
        const [giveaway] = state.giveaways.filter(g => g.id === id);
        await commit('setGiveaway', { ...giveaway, ...payload });
        return true;
      } catch (e) {
        return false;
      }
    },
    updateState: async ({ commit, state }, { id, state: giveawayState }) => {
      try {
        await GiveawayService.updateState(id, giveawayState);
        const [giveaway] = state.giveaways.filter(g => g.id === id);
        await commit('setGiveaway', { ...giveaway, state: giveawayState });
        return true;
      } catch (e) {
        return false;
      }
    },
    rollWinner: async (_, { id, winners }) => {
      try {
        const winner = await GiveawayService.rollWinner(id, winners);
        return winner;
      } catch (e) {
        return false;
      }
    },
    drawWinner: async (_, { id, winners }) => {
      try {
        const winner = await GiveawayService.drawWinner(id, winners);
        return winner;
      } catch (e) {
        return false;
      }
    },
    deleteGiveaway: async (_, id) => {
      try {
        await GiveawayService.delete(id);
        return true;
      } catch (e) {
        return false;
      }
    },
    export: async (_, id) => {
      try {
        await GiveawayService.export(id);
        return true;
      } catch (e) {
        return false;
      }
    },
    promoteGiveaway: async (_, id) => {
      console.log('Not yet implemented', id);
    },
  },
};
